import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Props } from './PersonalDetails.types';
import { SubPageTemplate } from '../../../partials';
import { Box, Columns } from 'modules/af_cm_ui_kit';
import validationSchema from './validationSchema';
import { StoreReadonlyField } from 'modules/cm/fields/EditableField';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import styles from './PersonalDetails.module.scss';
import PreviousAddressItem, { PreviousAddressWrapper } from './PreviousAddresses/PreviousAddressItem';
import { ApplicantAddress } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { yesNoOptions } from 'utils/form/formUtils';
import useCaseIsReadOnly from 'helpers/hooks/useCaseIsReadOnly/useCaseIsReadOnly';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../form/yup/reactHookFormResolver';
import { updateApplicant } from '../../../../../store/cases/update/actions';
import { addressesApi } from '../../../../../api';
import useSaveButtonClicked from '../../../hooks/useSaveButtonClicked';
import { saveCase, setErrorModal } from '../../../../../store/cases/actions';
import * as subPages from '../../../../../store/subPages';
import { SUBPAGES } from '../../../../../store/subPages/types';
import CheckDirty from '../../../../../components/CheckDirty/CheckDirty';

const PersonalDetails: React.FC<Props> = ({
  modulePrefix,
  data,
  initialData,
  applicantIndex,
  resources,
  caseId,
  applicantId,
}) => {
  const isCaseReadOnly = useCaseIsReadOnly();
  const dispatch = useDispatch();
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);

  const methods = useForm<Props['data']>({
    mode: 'onBlur',
    defaultValues: initialData,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { control, reset } = methods;

  useEffect(() => {
    reset(data, {
      keepDefaultValues: currentApplicantIndex === applicantIndex,
    });
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [data, applicantIndex, currentApplicantIndex, reset]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  const { fields: previousAddresses, append, remove: removePreviousAddress } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.APPLICANT_PREVIOUS_ADDRESSES,
  });

  const { getErrors } = usePepperError('applicants', applicantIndex);
  const { getErrors: getCurrentAddressErrors } = usePepperError('applicants', `${applicantIndex}.currentAddress`);

  const handleAddPreviousAddressClick = () => {
    addressesApi(caseId)(applicantId)
      .post()
      .then((res) => {
        append(res.data);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const handleApplicantAddressDelete = (item: ApplicantAddress, index: number) => {
    if (window.confirm('Are you sure?')) {
      addressesApi(caseId)(applicantId)
        .remove(item.id)
        .then(() => {
          removePreviousAddress(index);
        })
        .catch((err) => {
          dispatch(setErrorModal(err));
        });
    }
  };

  const { getErrors: getErrorsAddress } = usePepperError('applicants', `${applicantIndex}.previousAddresses`);

  return (
    <SubPageTemplate title="Personal details">
      <FormProvider<Props['data']> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(
                applicantIndex,
                SUBPAGES.APPLICANT_PERSONAL_DETAILS,
                isDirty,
              ),
            );
          }}
        />
        <form onSubmit={() => {}}>
          <Columns spacing={4}>
            <>
              <Box title="General">
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_TITLE]}
                  options={resources.title}
                  required
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_NAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_MIDDLE_NAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_SURNAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_PREVIOUS_NAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_PROOF_OF_ID]}
                  options={resources.proofOfId}
                  withDefaultOption={true}
                  required
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_DATE_OF_BIRTH]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <StoreReadonlyField
                  prefix={`${FIELDS.APPLICANTS}.${applicantIndex}.`}
                  field={fields[FIELDS.APPLICANT_AGE]}
                  variant={VARIANTS_TYPES.YEARS}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_GENDER]}
                  options={resources.gender}
                  required
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_MARITAL_STATUS]}
                  options={resources.martialStatus}
                  required
                />
              </Box>

              <Box title="Address history">
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_FLAT_NUMBER]}
                />
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_BUILDING_NAME]}
                />
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_BUILDING_NUMBER]}
                />
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_STREET]}
                />
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_CITY]}
                />
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_POSTCODE]}
                  variant={VARIANTS_TYPES.POSTCODE}
                />
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getCurrentAddressErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_CURRENT_ADDRESS][FIELDS.APPLICANT_ADDRESS_PROOF_OF_ADDRESS]}
                  options={resources.proofOfAddress}
                  withDefaultOption={true}
                  required
                />
                <hr className={styles.divider} />
                <PreviousAddressWrapper>
                  {previousAddresses.map((field, index) => (
                    <PreviousAddressItem
                      key={field.key}
                      item={field}
                      index={index}
                      modulePrefix={modulePrefix}
                      getErrors={getErrorsAddress}
                      isCaseReadOnly={isCaseReadOnly}
                      onAddressDelete={() => handleApplicantAddressDelete(field, index)}
                    />
                  ))}
                </PreviousAddressWrapper>

                <button
                  className={styles.button}
                  type="button"
                  style={{ marginTop: 12, marginBottom: -16 }}
                  disabled={isCaseReadOnly}
                  onClick={() => handleAddPreviousAddressClick()}>
                  Add previous address
                </button>
              </Box>
              <Box title="Power of Attorney">
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_NAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_PHONE_NUMBER]}
                  variant={VARIANTS_TYPES.PHONE}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_EMAIL]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_POSTCODE]}
                  variant={VARIANTS_TYPES.POSTCODE}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_FLAT_NUMBER]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NAME]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NUMBER]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_STREET]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][FIELDS.APPLICANT_POWER_OF_ATTORNEY_CITY]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={
                    fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][
                      FIELDS.APPLICANT_POWER_OF_ATTORNEY_HAS_CUSTOMISED_SERVICES
                    ]
                  }
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={
                    fields[FIELDS.APPLICANT_POWER_OF_ATTORNEY][
                      FIELDS.APPLICANT_POWER_OF_ATTORNEY_CUSTOMISED_SERVICES_REQUEST
                    ]
                  }
                />
              </Box>
            </>
            <>
              <Box title="Residency">
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_NATIONALITY]}
                  options={resources.nationalities}
                  searchable={true}
                  required
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={fields[FIELDS.APPLICANT_IS_UK_RESIDENT]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.CHECKBOX}
                  variant="inline"
                  field={fields[FIELDS.APPLICANT_HAS_INDEFINITE_LEAVE_TO_REMAIN]}
                />
              </Box>
              <Box title="Contact">
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_PHONE_HOME]}
                  variant={VARIANTS_TYPES.PHONE}
                  extraContent={[
                    {
                      position: 'bottom',
                      variant: 'component',
                      content: (
                        <UpdatableField
                          getErrors={getErrors}
                          modulePrefix={modulePrefix}
                          fieldType={INPUTS.CHECKBOX}
                          variant="inline"
                          field={fields[FIELDS.APPLICANT_IS_PHONE_HOME_PEPPER]}
                        />
                      ),
                    },
                  ]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_PHONE_MOBILE]}
                  variant={VARIANTS_TYPES.PHONE}
                  extraContent={[
                    {
                      position: 'bottom',
                      variant: 'component',
                      content: (
                        <UpdatableField
                          getErrors={getErrors}
                          modulePrefix={modulePrefix}
                          fieldType={INPUTS.CHECKBOX}
                          variant="inline"
                          field={fields[FIELDS.APPLICANT_IS_PHONE_MOBILE_PEPPER]}
                        />
                      ),
                    },
                  ]}
                />

                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_PHONE_WORK]}
                  variant={VARIANTS_TYPES.PHONE}
                  extraContent={[
                    {
                      position: 'bottom',
                      variant: 'component',
                      content: (
                        <UpdatableField
                          getErrors={getErrors}
                          modulePrefix={modulePrefix}
                          fieldType={INPUTS.CHECKBOX}
                          variant="inline"
                          field={fields[FIELDS.APPLICANT_IS_PHONE_WORK_PEPPER]}
                        />
                      ),
                    },
                  ]}
                />

                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_EMAIL]}
                  extraContent={[
                    {
                      position: 'bottom',
                      variant: 'component',
                      content: (
                        <UpdatableField
                          getErrors={getErrors}
                          modulePrefix={modulePrefix}
                          fieldType={INPUTS.CHECKBOX}
                          variant="inline"
                          field={fields[FIELDS.APPLICANT_IS_EMAIL_PEPPER]}
                        />
                      ),
                    },
                  ]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_ADDITIONAL_EMAIL]}
                  extraContent={[
                    {
                      position: 'bottom',
                      variant: 'component',
                      content: (
                        <UpdatableField
                          getErrors={getErrors}
                          modulePrefix={modulePrefix}
                          fieldType={INPUTS.CHECKBOX}
                          variant="inline"
                          field={fields[FIELDS.APPLICANT_ADDITIONAL_EMAIL_PEPPER]}
                        />
                      ),
                    },
                  ]}
                />
              </Box>
              <Box title="Disclosures">
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_IS_CRIMINAL]}
                  options={yesNoOptions}
                  required={true}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_CRIMINAL_CONVICTIONS_DETAIL]}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.APPLICANT_HAS_ADVERSE_CREDIT]}
                  options={yesNoOptions}
                  required={true}
                />
                <UpdatableField
                  getErrors={getErrors}
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.APPLICANT_ADVERSE_CREDIT_DETAILS]}
                />

                <Columns spacing={4}>
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_BANKRUPTCY_AMOUNT]}
                    variant={VARIANTS_TYPES.CURRENCY}
                  />
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE]}
                    variant={VARIANTS_TYPES.DATE}
                  />
                </Columns>
                <Columns spacing={4}>
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_CCJ_AMOUNT]}
                    variant={VARIANTS_TYPES.CURRENCY}
                  />
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_CCJ_LATEST_DATE]}
                    variant={VARIANTS_TYPES.DATE}
                  />
                </Columns>
                <Columns spacing={4}>
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_DEFAULT_AMOUNT]}
                    variant={VARIANTS_TYPES.CURRENCY}
                  />
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_DEFAULT_LAST_DATE]}
                    variant={VARIANTS_TYPES.DATE}
                  />
                </Columns>
                <Columns spacing={4}>
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_MORTGAGE_ARREARS_AMOUNT]}
                    variant={VARIANTS_TYPES.CURRENCY}
                  />
                  <UpdatableField
                    getErrors={getErrors}
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE]}
                    variant={VARIANTS_TYPES.DATE}
                  />
                </Columns>
              </Box>
            </>
          </Columns>
        </form>
      </FormProvider>
    </SubPageTemplate>
  );
};

export default PersonalDetails;
